import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    age: '',
    level: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6ivvdgdenotc3br4b90', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', position: '', age: '', level: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <section id="contact" className="py-16 md:py-24 bg-gray-900">
      <div className="max-w-7xl mx-auto px-5 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          <div>
            <span className="text-teal-400 text-xs font-bold uppercase tracking-wider">Get Started</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mt-3 mb-5">Schedule Your Evaluation</h2>
            <p className="text-base text-gray-300 leading-relaxed mb-7">
              Ready to take your game to the next level? Start with a comprehensive on-site evaluation at our Corsicana facility.
            </p>

            <div className="space-y-5 mb-7">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-11 h-11 flex items-center justify-center bg-teal-500/20 rounded-lg">
                  <i className="ri-phone-line text-xl text-teal-400"></i>
                </div>
                <div>
                  <h3 className="text-base font-bold text-white mb-1">Phone</h3>
                  <a href="tel:+12544245723" className="text-gray-400 hover:text-teal-400 transition-colors text-sm">
                    (254) 424-5723
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-11 h-11 flex items-center justify-center bg-teal-500/20 rounded-lg">
                  <i className="ri-map-pin-line text-xl text-teal-400"></i>
                </div>
                <div>
                  <h3 className="text-base font-bold text-white mb-1">Location</h3>
                  <p className="text-gray-400 text-sm">1216 S 7th Street Suite C</p>
                  <p className="text-gray-400 text-sm">Corsicana, TX 75110</p>
                  <p className="text-gray-400 text-sm">50 minutes south of Dallas</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-11 h-11 flex items-center justify-center bg-teal-500/20 rounded-lg">
                  <i className="ri-time-line text-xl text-teal-400"></i>
                </div>
                <div>
                  <h3 className="text-base font-bold text-white mb-1">Training Hours</h3>
                  <p className="text-gray-400 text-sm">By Appointment Only</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-11 h-11 flex items-center justify-center bg-teal-500/20 rounded-lg">
                  <i className="ri-share-line text-xl text-teal-400"></i>
                </div>
                <div>
                  <h3 className="text-base font-bold text-white mb-2">Follow Us</h3>
                  <div className="flex items-center gap-3">
                    <a
                      href="https://www.instagram.com/elite_performance_lab"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-9 h-9 flex items-center justify-center bg-teal-500/20 hover:bg-teal-500/40 border border-teal-500/30 hover:border-teal-400 rounded-lg transition-all cursor-pointer group"
                      aria-label="Instagram"
                    >
                      <i className="ri-instagram-line text-lg text-teal-400 group-hover:text-teal-300"></i>
                    </a>
                    <a
                      href="https://www.facebook.com/ElitePerformanceLab1"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-9 h-9 flex items-center justify-center bg-teal-500/20 hover:bg-teal-500/40 border border-teal-500/30 hover:border-teal-400 rounded-lg transition-all cursor-pointer group"
                      aria-label="Facebook"
                    >
                      <i className="ri-facebook-line text-lg text-teal-400 group-hover:text-teal-300"></i>
                    </a>
                    <a
                      href="https://www.youtube.com/@eliteperformancelab"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-9 h-9 flex items-center justify-center bg-teal-500/20 hover:bg-teal-500/40 border border-teal-500/30 hover:border-teal-400 rounded-lg transition-all cursor-pointer group"
                      aria-label="YouTube"
                    >
                      <i className="ri-youtube-line text-lg text-teal-400 group-hover:text-teal-300"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-teal-500/10 border border-teal-500/30 rounded-xl overflow-hidden mb-7">
              <iframe
                title="Elite Performance Lab Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3363.0!2d-96.4688!3d32.0957!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x864f3b1e1e1e1e1f%3A0x0!2s1216+S+7th+St+Suite+C%2C+Corsicana%2C+TX+75110!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus&q=1216+S+7th+Street+Suite+C,+Corsicana,+TX+75110"
                width="100%"
                height="220"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full"
              ></iframe>
              <div className="px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <i className="ri-map-pin-2-fill text-teal-400 text-base"></i>
                  <span className="text-gray-300 text-sm">1216 S 7th Street Suite C, Corsicana, TX 75110</span>
                </div>
                <a
                  href="https://www.google.com/maps/dir/?api=1&destination=1216+S+7th+Street+Suite+C,+Corsicana,+TX+75110"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-teal-400 hover:text-teal-300 text-xs font-semibold whitespace-nowrap cursor-pointer transition-colors flex items-center gap-1"
                >
                  <i className="ri-navigation-line text-sm"></i>
                  Get Directions
                </a>
              </div>
            </div>

            <div className="bg-teal-500/10 border border-teal-500/30 rounded-xl p-5">
              <h3 className="text-base font-bold text-white mb-2">Important Note</h3>
              <p className="text-gray-300 text-sm leading-relaxed">
                We require an on-site evaluation and initial workouts before beginning any remote training. This ensures we have accurate baseline data to create truly personalized programs.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-2xl">
            <h3 className="text-xl sm:text-2xl font-black text-gray-900 mb-5">Request Evaluation</h3>
            
            <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                    placeholder="Enter your full name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div>
                  <label htmlFor="position" className="block text-sm font-semibold text-gray-700 mb-2">
                    Position *
                  </label>
                  <select
                    id="position"
                    name="position"
                    value={formData.position}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all cursor-pointer"
                  >
                    <option value="">Select your position</option>
                    <option value="Pitcher">Pitcher</option>
                    <option value="Catcher">Catcher</option>
                    <option value="Infielder">Infielder</option>
                    <option value="Outfielder">Outfielder</option>
                    <option value="Two-Way Player">Two-Way Player</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="age" className="block text-sm font-semibold text-gray-700 mb-2">
                      Age *
                    </label>
                    <input
                      type="number"
                      id="age"
                      name="age"
                      value={formData.age}
                      onChange={handleChange}
                      required
                      min={8}
                      max={30}
                      className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                      placeholder="e.g. 16"
                    />
                  </div>

                  <div>
                    <label htmlFor="level" className="block text-sm font-semibold text-gray-700 mb-2">
                      Level *
                    </label>
                    <select
                      id="level"
                      name="level"
                      value={formData.level}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all cursor-pointer"
                    >
                      <option value="">Select level</option>
                      <option value="Jr. High">Jr. High</option>
                      <option value="High School">High School</option>
                      <option value="College">College</option>
                      <option value="Independent / Pro">Independent / Pro</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    Tell us about your goals
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all resize-none"
                    placeholder="Share your training goals and what you hope to achieve..."
                  />
                  <div className="text-xs text-gray-500 mt-1 text-right">
                    {formData.message.length}/500 characters
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-teal-500 text-white text-base font-bold rounded-lg hover:bg-teal-600 transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Submitting...' : 'Schedule Evaluation'}
                </button>

                {submitStatus === 'success' && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-800 font-semibold">
                      Thank you! We'll contact you soon to schedule your evaluation.
                    </p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-800 font-semibold">
                      Something went wrong. Please try again or contact us directly.
                    </p>
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}